<template>
  <base-dropdown @shown="scrollToSelected">
    <template #default="{ toggle }">
      <input
        :id="id"
        ref="trigger"
        v-model="displayTime"
        class="form-control form-control_size_time"
        type="text"
        readonly
        @click="toggle"
      >
    </template>
    <template #content>
      <div :class="$style.select">
        <ul
          ref="hoursList"
          class="hours"
          :class="$style.list"
        >
          <li
            v-for="hr in hours"
            :key="hr"
            :class="{
              [$style.active]: hour === hr,
              [$style.disabled]: isHourDisabled(hr)
            }"
            @click.stop="select('hour', hr)"
            v-text="hr"
          />
        </ul>
        <ul
          ref="minutesList"
          :class="[$style.list, $style.minutes]"
        >
          <li
            v-for="m in minutes"
            :key="m"
            :class="{
              [$style.active]: minute === m,
              [$style.disabled]: isMinuteDisabled(m)
            }"
            @click.stop="select('minute', m)"
            v-text="m"
          />
        </ul>
      </div>
    </template>
  </base-dropdown>
</template>

<script>
import { nanoid } from 'nanoid';
import BaseDropdown from '@/shared/legacy/ui/base-dropdown/base-dropdown.vue';

const CONFIG = {
  HOUR_TOKENS: ['HH', 'H', 'hh', 'h', 'kk', 'k'],
  MINUTE_TOKENS: ['mm', 'm'],
  SECOND_TOKENS: ['ss', 's'],
  APM_TOKENS: ['A', 'a']
};

export default {
  name: 'VueTimepicker',

  components: { BaseDropdown },

  props: {
    value: { type: Object },
    // Lower bound of the selectable time, in the same shape as `value`
    // (keys are format tokens, e.g. `{ HH: '14', mm: '20' }`)
    minValue: { type: Object },
    format: { type: String },
    minuteInterval: { type: Number },
    secondInterval: { type: Number },
    id: {
      type: String,
      default() {
        return nanoid();
      }
    }
  },

  emits: ['changeTime', 'change', 'input'],

  data() {
    return {
      hours: [],
      minutes: [],
      seconds: [],
      apms: [],
      muteWatch: false,
      hourType: 'HH',
      minuteType: 'mm',
      secondType: '',
      apmType: '',
      hour: '',
      minute: '',
      second: '',
      apm: '',
      fullValues: undefined
    };
  },

  computed: {
    displayTime() {
      let formatString = String(this.format || 'HH:mm');
      if (this.hour) {
        formatString = formatString.replace(new RegExp(this.hourType, 'g'), this.hour);
      }
      if (this.minute) {
        formatString = formatString.replace(new RegExp(this.minuteType, 'g'), this.minute);
      }
      if (this.second && this.secondType) {
        formatString = formatString.replace(new RegExp(this.secondType, 'g'), this.second);
      }
      if (this.apm && this.apmType) {
        formatString = formatString.replace(new RegExp(this.apmType, 'g'), this.apm);
      }
      return formatString;
    },

    minuteOptions() {
      return this.minutes.map(Number).sort((a, b) => a - b);
    },

    // `minValue` expressed as minutes since midnight, or `null` when there is no lower bound
    minTotalMinutes() {
      return this.parseTotalMinutes(this.minValue);
    },

    // The earliest time that can actually be picked: `minValue` rounded up to the
    // nearest available minute option (matters when `minute-interval` is set)
    allowedTotalMinutes() {
      if (this.minTotalMinutes === null) {
        return null;
      }

      const hour = Math.floor(this.minTotalMinutes / 60);
      const minute = this.minTotalMinutes % 60;
      const nextMinute = this.minuteOptions.find((option) => option >= minute);

      if (nextMinute !== undefined) {
        return hour * 60 + nextMinute;
      }
      return (hour + 1) * 60 + (this.minuteOptions[0] || 0);
    },

    allowedHour() {
      return this.allowedTotalMinutes === null ? null : Math.floor(this.allowedTotalMinutes / 60);
    },

    firstAvailableHour() {
      return this.hours.find((option) => !this.isHourDisabled(option));
    },

    firstAvailableMinute() {
      if (this.allowedTotalMinutes === null) {
        return this.minutes[0];
      }
      const allowedMinute = this.allowedTotalMinutes % 60;
      return this.minutes.find((option) => Number(option) >= allowedMinute) ?? this.minutes[0];
    }
  },

  watch: {
    format: 'renderFormat',
    minValue: {
      handler: 'clampToMinValue',
      deep: true
    },
    minuteInterval(newInteval) {
      this.renderList('minute', newInteval);
    },
    secondInterval(newInteval) {
      this.renderList('second', newInteval);
    },
    value: 'readValues',
    displayTime: 'fillValues'
  },

  mounted() {
    this.renderFormat();
  },

  methods: {
    formatValue(type, i) {
      switch (type) {
        case 'H':
        case 'm':
        case 's':
          return String(i);
        case 'HH':
        case 'mm':
        case 'ss':
          return i < 10 ? `0${i}` : String(i);
        case 'h':
        case 'k':
          return String(i + 1);
        case 'hh':
        case 'kk':
          return i + 1 < 10 ? `0${i + 1}` : String(i + 1);
        default:
          return '';
      }
    },

    checkAcceptingType(validValues, formatString, fallbackValue) {
      if (!validValues || !formatString || !formatString.length) {
        return '';
      }
      for (let i = 0; i < validValues.length; i++) {
        if (formatString.includes(validValues[i])) {
          return validValues[i];
        }
      }
      return fallbackValue || '';
    },

    renderFormat(newFormat) {
      newFormat = newFormat || this.format;
      if (!newFormat || !newFormat.length) {
        newFormat = 'HH:mm';
      }

      this.hourType = this.checkAcceptingType(CONFIG.HOUR_TOKENS, newFormat, 'HH');
      this.minuteType = this.checkAcceptingType(CONFIG.MINUTE_TOKENS, newFormat, 'mm');
      this.secondType = this.checkAcceptingType(CONFIG.SECOND_TOKENS, newFormat);
      this.apmType = this.checkAcceptingType(CONFIG.APM_TOKENS, newFormat);

      this.renderHoursList();
      this.renderList('minute');

      if (this.secondType) {
        this.renderList('second');
      }

      if (this.apmType) {
        this.renderApmList();
      }

      this.$nextTick(() => {
        this.readValues();
      });
    },

    renderHoursList() {
      const hoursCount = this.hourType === 'h' || this.hourType === 'hh' ? 12 : 24;
      this.hours = [];
      for (let i = 0; i < hoursCount; i++) {
        this.hours.push(this.formatValue(this.hourType, i));
      }
    },

    renderList(listType, interval) {
      if (listType === 'second') {
        interval = interval || this.secondInterval;
      } else if (listType === 'minute') {
        interval = interval || this.minuteInterval;
      } else {
        return;
      }

      if (interval === 0) {
        interval = 60;
      } else if (interval > 60) {
        window.console.warn(
          `\`${listType}-interval\` should be less than 60. Current value is`,
          interval
        );
        interval = 1;
      } else if (interval < 1) {
        window.console.warn(
          `\`${listType}-interval\` should be NO less than 1. Current value is`,
          interval
        );
        interval = 1;
      } else if (!interval) {
        interval = 1;
      }

      if (listType === 'minute') {
        this.minutes = [];
      } else {
        this.seconds = [];
      }

      for (let i = 0; i < 60; i += interval) {
        if (listType === 'minute') {
          this.minutes.push(this.formatValue(this.minuteType, i));
        } else {
          this.seconds.push(this.formatValue(this.secondType, i));
        }
      }
    },

    renderApmList() {
      this.apms = [];
      if (!this.apmType) {
        return;
      }
      this.apms = this.apmType === 'A' ? ['AM', 'PM'] : ['am', 'pm'];
    },

    readValues() {
      if (!this.value || this.muteWatch) {
        return;
      }

      const timeValue = JSON.parse(JSON.stringify(this.value || {}));

      const values = Object.keys(timeValue);
      if (values.length === 0) {
        return;
      }

      if (values.includes(this.hourType)) {
        this.hour = timeValue[this.hourType];
      }

      if (values.includes(this.minuteType)) {
        this.minute = timeValue[this.minuteType];
      }

      if (values.includes(this.secondType)) {
        this.second = timeValue[this.secondType];
      } else {
        this.second = 0;
      }

      if (values.includes(this.apmType)) {
        this.apm = timeValue[this.apmType];
      }

      // An incoming value below `minValue` is rounded up to the minimum
      this.clampToMinValue();

      this.fillValues();
    },

    // Converts an hour of the current `hourType` into a 0..23 hour
    toTwentyFourHour(hour, apm) {
      if (Number.isNaN(hour)) {
        return Number.NaN;
      }
      if (this.isTwelveHours(this.hourType)) {
        const isPm = String(apm || this.apm || '').toLowerCase() === 'pm';
        return (hour % 12) + (isPm ? 12 : 0);
      }
      // `kk`/`k` count hours as 1..24, so midnight arrives here as 24
      return hour % 24;
    },

    // Reads a `{ [token]: value }` time object into minutes since midnight
    parseTotalMinutes(source) {
      if (!source) {
        return null;
      }

      const rawHour = source[this.hourType];
      if (rawHour === undefined || rawHour === null || rawHour === '') {
        return null;
      }

      const hour = this.toTwentyFourHour(Number(rawHour), source[this.apmType]);
      if (Number.isNaN(hour)) {
        return null;
      }

      const rawMinute = source[this.minuteType];
      const minute = rawMinute === undefined || rawMinute === null || rawMinute === ''
        ? 0
        : Number(rawMinute);

      return hour * 60 + (Number.isNaN(minute) ? 0 : minute);
    },

    setTotalMinutes(totalMinutes) {
      const hour = Math.floor(totalMinutes / 60) % 24;
      const minute = totalMinutes % 60;

      if (this.isTwelveHours(this.hourType)) {
        const hourOnTwelve = hour % 12 === 0 ? 12 : hour % 12;
        this.hour = this.formatValue(this.hourType, hourOnTwelve - 1);
        if (this.apmType) {
          const apm = hour < 12 ? 'am' : 'pm';
          this.apm = this.apmType === 'A' ? apm.toUpperCase() : apm;
        }
      } else if (this.hourType === 'k' || this.hourType === 'kk') {
        this.hour = this.formatValue(this.hourType, hour === 0 ? 23 : hour - 1);
      } else {
        this.hour = this.formatValue(this.hourType, hour);
      }

      this.minute = this.formatValue(this.minuteType, minute);
    },

    clampToMinValue() {
      if (this.allowedTotalMinutes === null) {
        return;
      }

      const current = this.parseTotalMinutes({
        [this.hourType]: this.hour,
        [this.minuteType]: this.minute,
        [this.apmType]: this.apm
      });

      if (current === null || current >= this.allowedTotalMinutes) {
        return;
      }

      this.setTotalMinutes(this.allowedTotalMinutes);
    },

    // Brings the selected option into view when the dropdown opens; with nothing selected
    // it falls back to the first option that is not cut off by `minValue`
    scrollToSelected() {
      this.$nextTick(() => {
        this.scrollListTo(this.$refs.hoursList, this.hours, this.hour || this.firstAvailableHour);
        this.scrollListTo(
          this.$refs.minutesList,
          this.minutes,
          this.minute || this.firstAvailableMinute
        );
      });
    },

    scrollListTo(list, options, value) {
      if (!list) {
        return;
      }

      const index = options.indexOf(value);
      const item = index > 0 ? list.children[index] : undefined;
      list.scrollTop = item ? item.offsetTop - list.offsetTop : 0;
    },

    isHourDisabled(hourOption) {
      if (this.allowedHour === null) {
        return false;
      }
      return this.toTwentyFourHour(Number(hourOption)) < this.allowedHour;
    },

    isMinuteDisabled(minuteOption) {
      if (this.allowedHour === null || this.hour === '') {
        return false;
      }

      const hour = this.toTwentyFourHour(Number(this.hour));
      if (hour > this.allowedHour) {
        return false;
      }
      if (hour < this.allowedHour) {
        return true;
      }
      return Number(minuteOption) < this.allowedTotalMinutes % 60;
    },

    fillValues() {
      // Emit to parent
      const model = {
        HH: this.hour,
        mm: this.minute
      };
      this.$emit('changeTime', model);

      // Display values
      const fullValues = {};

      const baseHour = this.hour;
      const baseHourType = this.hourType;

      const hourValue = baseHour || baseHour === 0 ? Number(baseHour) : '';
      const baseOnTwelveHours = this.isTwelveHours(baseHourType);
      const apmValue = baseOnTwelveHours && this.apm ? String(this.apm).toLowerCase() : false;

      CONFIG.HOUR_TOKENS.forEach((token) => {
        if (token === baseHourType) {
          fullValues[token] = baseHour;
          return;
        }

        let value;
        let apm;
        switch (token) {
          case 'H':
          case 'HH':
            if (!String(hourValue).length) {
              fullValues[token] = '';
              return;
            } else if (baseOnTwelveHours) {
              if (apmValue === 'pm') {
                value = hourValue < 12 ? hourValue + 12 : hourValue;
              } else {
                value = hourValue % 12;
              }
            } else {
              value = hourValue % 24;
            }
            fullValues[token] = token === 'HH' && value < 10 ? `0${value}` : String(value);
            break;
          case 'k':
          case 'kk':
            if (!String(hourValue).length) {
              fullValues[token] = '';
              return;
            } else if (baseOnTwelveHours) {
              if (apmValue === 'pm') {
                value = hourValue < 12 ? hourValue + 12 : hourValue;
              } else {
                value = hourValue === 12 ? 24 : hourValue;
              }
            } else {
              value = hourValue === 0 ? 24 : hourValue;
            }
            fullValues[token] = token === 'kk' && value < 10 ? `0${value}` : String(value);
            break;
          case 'h':
          case 'hh':
            if (apmValue) {
              value = hourValue;
              apm = apmValue || 'am';
            } else if (!String(hourValue).length) {
              fullValues[token] = '';
              fullValues.a = '';
              fullValues.A = '';
              return;
            } else if (hourValue > 11) {
              apm = 'pm';
              value = hourValue === 12 ? 12 : hourValue % 12;
            } else {
              if (baseOnTwelveHours) {
                apm = '';
              } else {
                apm = 'am';
              }
              value = hourValue % 12 === 0 ? 12 : hourValue;
            }
            fullValues[token] = token === 'hh' && value < 10 ? `0${value}` : String(value);
            fullValues.a = apm;
            fullValues.A = apm.toUpperCase();
            break;
        }
      });

      if (this.minute || this.minute === 0) {
        const minuteValue = Number(this.minute);
        fullValues.m = String(minuteValue);
        fullValues.mm = minuteValue < 10 ? `0${minuteValue}` : String(minuteValue);
      } else {
        fullValues.m = '';
        fullValues.mm = '';
      }

      if (this.second || this.second === 0) {
        const secondValue = Number(this.second);
        fullValues.s = String(secondValue);
        fullValues.ss = secondValue < 10 ? `0${secondValue}` : String(secondValue);
      } else {
        fullValues.s = '';
        fullValues.ss = '';
      }

      this.fullValues = fullValues;
      this.updateTimeValue(fullValues);
      this.$emit('change', { data: fullValues });
    },

    updateTimeValue(fullValues) {
      this.muteWatch = true;

      const baseTimeValue = JSON.parse(JSON.stringify(this.value || {}));
      const timeValue = {};

      Object.keys(baseTimeValue).forEach((key) => {
        timeValue[key] = fullValues[key];
      });

      this.$emit('input', timeValue);

      // Используется setTimeout(), чтобы 'muteWatch' не включился раньше требуемого;
      // nextTick() тут не работает - если только не писать 2 вложенных nextTick'а,
      // но такая конструкция хуже выглядит
      setTimeout(() => {
        this.muteWatch = false;
      }, 0);
    },

    isTwelveHours(token) {
      return token === 'h' || token === 'hh';
    },

    select(type, value) {
      if (type === 'hour') {
        if (this.isHourDisabled(value)) {
          return;
        }
        this.hour = value;
      } else if (type === 'minute') {
        if (this.isMinuteDisabled(value)) {
          return;
        }
        this.minute = value;
      } else if (type === 'second') {
        this.second = value;
      } else if (type === 'apm') {
        this.apm = value;
      }

      // Picking the minimum hour with an earlier minute already selected pulls the minute up
      this.clampToMinValue();
    },

    clearTime() {
      this.hour = '';
      this.minute = '';
      this.second = '';
      this.apm = '';
    }
  }
};
</script>

<style module>
.select {
  height: 200px;
  width: $smallerWidth;
  overflow: hidden;
  display: flex;
  flex-flow: row nowrap;
  align-items: stretch;
  justify-content: space-between;
}

.list {
  padding: 0;
  margin: 0;
  list-style: none;

  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;

  &.minutes {
    border-left: 1px solid $whiteColor;
  }

  li {
    text-align: center;
    height: 32px;
    line-height: 32px;
    color: $textColor;

    &:not(.hint):hover {
      background: rgba($vantaBlackColor, 0.08);
      cursor: pointer;
    }

    &.active,
    &.active:hover {
      background: $blueColor;
      color: $whiteColor;
    }

    &.disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }
}
</style>

<i18n lang="json">{}</i18n>
